import { sortOrganisations } from "@/utils/organisationSort";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash, Users, Building2, Plus, ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

// PostgREST caps a single response at 1000 rows regardless of the requested
// range, so paginate until we've drained the table.
async function fetchAllPaged<T>(
  build: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: any }>,
  batch = 1000,
): Promise<T[]> {
  const out: T[] = [];
  let from = 0;
  // Hard safety cap in case something loops unexpectedly.
  for (let i = 0; i < 100; i++) {
    const to = from + batch - 1;
    const { data, error } = await build(from, to);
    if (error) throw error;
    if (!data || data.length === 0) break;
    out.push(...data);
    if (data.length < batch) break;
    from += batch;
  }
  return out;
}

interface GroupedAssignment {
  device_id: string;
  device_name: string | null;
  device_serial: string | null;
  organizations: Array<{
    id: string;
    name: string;
  }>;
  assignments: Array<{
    id: string;
    user_id: string | null;
    username: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    display_name: string;
    assigned_at: string;
    user_role: string | null;
    user_organization: string | null;
    user_organization_id: string | null;
  }>;
}

interface DeviceGroupedAssignmentTableProps {
  quickAddDevice?: {
    deviceId: string;
    deviceName: string;
  } | null;
  onQuickAddDeviceChange?: (device: { deviceId: string; deviceName: string } | null) => void;
}

export const DeviceGroupedAssignmentTable = ({ 
  quickAddDevice, 
  onQuickAddDeviceChange 
}: DeviceGroupedAssignmentTableProps) => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    deviceId: "all",
    userId: "all",
    adminOnly: false,
    organizationId: "all",
    showUnassigned: false,
    searchText: "",
  });

  const { data: groupedAssignments, refetch: refetchAssignments } = useQuery({
    queryKey: ["deviceGroupedAssignments", filters],
    queryFn: async () => {
      // Get all devices with their assignments (paginated to bypass the
      // 1000-row PostgREST cap).
      const devices = await fetchAllPaged<{
        device_id: string;
        device_name: string | null;
        device_serial: string | null;
      }>((from, to) =>
        supabase
          .from("device_details")
          .select("device_id, device_name, device_serial")
          .range(from, to),
      );

      const assignments = await fetchAllPaged<{
        id: string;
        device_id: string;
        user_id: string | null;
        assigned_at: string;
      }>((from, to) =>
        supabase
          .from("device_assignments")
          .select(`id, device_id, user_id, assigned_at`)
          .range(from, to),
      );

      const userRoles = await fetchAllPaged<{
        user_id: string;
        role: string | null;
        organization_id: string | null;
      }>((from, to) =>
        supabase
          .from("user_roles")
          .select("user_id, role, organization_id")
          .range(from, to),
      );

      const { data: organizations } = await supabase
        .from("organizations")
        .select("id, name");

      // Get profiles for all users referenced in assignments
      const userIds = assignments?.map(a => a.user_id).filter(Boolean) || [];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, username, first_name, last_name, email")
        .in("user_id", userIds);

      if (!devices) return [];

      // Create maps for easier lookup
      const orgMap = new Map(organizations?.map(org => [org.id, org.name]) || []);
      const roleMap = new Map(userRoles?.map(role => [role.user_id, role]) || []);
      const profileMap = new Map(profiles?.map(profile => [profile.user_id, profile]) || []);

      // Group assignments by device
      const grouped: GroupedAssignment[] = devices.map(device => {
        const deviceAssignments = assignments?.filter(a => a.device_id === device.device_id) || [];
        
        // Get all unique organizations for this device
        const deviceOrganizations = new Set<string>();
        deviceAssignments.forEach(assignment => {
          const userRole = roleMap.get(assignment.user_id || '');
          if (userRole?.organization_id) {
            deviceOrganizations.add(userRole.organization_id);
          }
        });

        const organizations = Array.from(deviceOrganizations).map(orgId => ({
          id: orgId,
          name: orgMap.get(orgId) || 'Unknown Organization'
        }));

        return {
          device_id: device.device_id,
          device_name: device.device_name,
          device_serial: (device as any).device_serial ?? null,
          organizations,
          assignments: deviceAssignments.map(assignment => {
            const userRole = roleMap.get(assignment.user_id || '');
            const userOrgName = userRole?.organization_id ? orgMap.get(userRole.organization_id) : null;
            const profile = profileMap.get(assignment.user_id || '');
            
            const displayName = profile?.username ||
              (profile?.first_name && profile?.last_name 
                ? `${profile.first_name} ${profile.last_name}`
                : profile?.email || assignment.user_id || 'Unknown User');

            return {
              id: assignment.id,
              user_id: assignment.user_id,
              username: profile?.username || null,
              first_name: profile?.first_name || null,
              last_name: profile?.last_name || null,
              email: profile?.email || null,
              display_name: displayName,
              assigned_at: assignment.assigned_at,
              user_role: userRole?.role || null,
              user_organization: userOrgName || null,
              user_organization_id: userRole?.organization_id || null,
            };
          }),
        };
      });

      return grouped;
    },
  });

  const { data: devices } = useQuery({
    queryKey: ["devices"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("device_details")
        .select("device_id, device_name, device_serial");
      
      if (error) throw error;
      return data;
    },
  });

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, username, first_name, last_name, email");
      
      if (error) throw error;
      return data;
    },
  });

  const { data: organizations } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name");
      
      if (error) throw error;
      return data;
    },
  });

  const filteredGroupedAssignments = groupedAssignments?.filter((deviceGroup) => {
    // Free-text search on device name, id, serial
    if (filters.searchText.trim()) {
      const q = filters.searchText.toLowerCase().trim();
      const name = deviceGroup.device_name?.toLowerCase() || "";
      const id = deviceGroup.device_id?.toLowerCase() || "";
      const serial = deviceGroup.device_serial?.toLowerCase() || "";
      if (!name.includes(q) && !id.includes(q) && !serial.includes(q)) {
        return false;
      }
    }

    // Filter by device
    if (filters.deviceId !== "all" && deviceGroup.device_id !== filters.deviceId) {
      return false;
    }

    // Filter by organization - check if ANY assignment belongs to the selected organization
    if (filters.organizationId !== "all") {
      const hasOrganization = deviceGroup.assignments.some(assignment => 
        assignment.user_organization_id === filters.organizationId
      );
      if (!hasOrganization) return false;
    }

    // Show unassigned devices option (skip this gate when searching so users can find any device)
    if (!filters.searchText.trim()) {
      if (filters.showUnassigned && deviceGroup.assignments.length > 0) {
        return false;
      }
      if (!filters.showUnassigned && deviceGroup.assignments.length === 0) {
        return false;
      }
    }

    // Filter by user
    if (filters.userId !== "all") {
      const hasUser = deviceGroup.assignments.some(a => a.user_id === filters.userId);
      if (!hasUser) return false;
    }

    // Filter by admin only
    if (filters.adminOnly) {
      const hasAdmin = deviceGroup.assignments.some(a => a.user_role === 'admin');
      if (!hasAdmin) return false;
    }

    return true;
  });

  const handleDeleteAssignment = async (id: string) => {
    try {
      const { error } = await supabase
        .from("device_assignments")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Assignment deleted successfully");
      refetchAssignments();
    } catch (error) {
      console.error("Error deleting assignment:", error);
      toast.error("Failed to delete assignment");
    } finally {
      setDeleteId(null);
    }
  };

  const handleQuickAddUser = (deviceId: string, deviceName: string) => {
    onQuickAddDeviceChange?.({ deviceId, deviceName });
    // Scroll to the add section at the top
    document.querySelector('.add-assignment-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleCardExpansion = (deviceId: string) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(deviceId)) {
      newExpanded.delete(deviceId);
    } else {
      newExpanded.add(deviceId);
    }
    setExpandedCards(newExpanded);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-end border-b pb-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="grid gap-2">
            <label className="hidden md:block text-sm font-medium">Search Devices</label>
            <div className="relative w-[260px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, serial, or ID..."
                value={filters.searchText}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, searchText: e.target.value }))
                }
                className="pl-8 pr-8"
              />
              {filters.searchText && (
                <button
                  type="button"
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, searchText: "" }))
                  }
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="grid gap-2">
            <label className="hidden md:block text-sm font-medium">Filter by Device</label>
            <Select
              value={filters.deviceId}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, deviceId: value }))
              }
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All devices" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All devices</SelectItem>
                {devices?.map((device) => (
                  <SelectItem key={device.device_id} value={device.device_id}>
                    {device.device_name || device.device_serial || device.device_id} ({device.device_serial || device.device_id.slice(0, 8)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <label className="hidden md:block text-sm font-medium">Filter by User</label>
            <Select
              value={filters.userId}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, userId: value }))
              }
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                 {users?.map((user) => {
                   const displayName = user.username || 
                     (user.first_name && user.last_name 
                       ? `${user.first_name} ${user.last_name}`
                       : user.email || user.user_id);
                   return (
                     <SelectItem key={user.user_id} value={user.user_id}>
                       {displayName}
                     </SelectItem>
                   );
                 })}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <label className="hidden md:block text-sm font-medium">Filter by Organization</label>
            <Select
              value={filters.organizationId}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, organizationId: value }))
              }
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All organizations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All organizations</SelectItem>
                {sortOrganisations(organizations ?? [], (org) => org.name).map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Admins Only checkbox underneath organizations filter on mobile */}
            <div className="flex md:hidden items-center space-x-2 mt-2">
              <Checkbox
                id="adminOnly-mobile"
                checked={filters.adminOnly}
                onCheckedChange={(checked) =>
                  setFilters((prev) => ({ ...prev, adminOnly: checked === true }))
                }
              />
              <label
                htmlFor="adminOnly-mobile"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Admins Only
              </label>
            </div>
          </div>

          {/* Desktop layout - checkboxes inline */}
          <div className="hidden md:flex items-center space-x-2">
            <Checkbox
              id="adminOnly"
              checked={filters.adminOnly}
              onCheckedChange={(checked) =>
                setFilters((prev) => ({ ...prev, adminOnly: checked === true }))
              }
            />
            <label
              htmlFor="adminOnly"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Admins Only
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="showUnassigned"
              checked={filters.showUnassigned}
              onCheckedChange={(checked) =>
                setFilters((prev) => ({ ...prev, showUnassigned: checked === true }))
              }
            />
            <label
              htmlFor="showUnassigned"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Show Unassigned Only
            </label>
          </div>
        </div>

        <Button
          variant="outline"
          className="hidden md:inline-flex"
          onClick={() => setFilters({ 
            deviceId: "all", 
            userId: "all", 
            adminOnly: false, 
            organizationId: "all",
            showUnassigned: false,
            searchText: "",
          })}
        >
          Clear Filters
        </Button>
      </div>

      <div className="grid gap-4">
        {filteredGroupedAssignments?.map((deviceGroup) => (
          <Card key={deviceGroup.device_id} className="w-full">
            <CardHeader className="pb-3">
              <div 
                className="flex items-center justify-between cursor-pointer md:cursor-default"
                onClick={() => toggleCardExpansion(deviceGroup.device_id)}
              >
                <CardTitle className="text-lg flex items-center gap-2">
                  <span>{deviceGroup.device_name || deviceGroup.device_id}</span>
                  <div className="hidden md:flex gap-1">
                    {deviceGroup.organizations.map((org) => (
                      <Badge key={org.id} variant="secondary" className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {org.name}
                      </Badge>
                    ))}
                  </div>
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="hidden md:flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {deviceGroup.assignments.length} user{deviceGroup.assignments.length !== 1 ? 's' : ''}
                  </Badge>
                  {/* Mobile expand/collapse indicator */}
                  <div className="md:hidden">
                    {expandedCards.has(deviceGroup.device_id) ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickAddUser(deviceGroup.device_id, deviceGroup.device_name || deviceGroup.device_id);
                    }}
                    className="flex items-center gap-1 bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90"
                  >
                    <Plus className="h-3 w-3" />
                    Add User
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">Device ID: {deviceGroup.device_id}</p>
              
              {/* Mobile expanded details */}
              {expandedCards.has(deviceGroup.device_id) && (
                <div className="md:hidden mt-3 pt-3 border-t space-y-2 animate-fade-in">
                  {/* Organizations */}
                  {deviceGroup.organizations.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {deviceGroup.organizations.map((org) => (
                        <Badge key={org.id} variant="secondary" className="flex items-center gap-1 text-xs">
                          <Building2 className="h-3 w-3" />
                          {org.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {/* User count */}
                  <Badge variant="outline" className="flex items-center gap-1 w-fit">
                    <Users className="h-3 w-3" />
                    {deviceGroup.assignments.length} user{deviceGroup.assignments.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {quickAddDevice?.deviceId === deviceGroup.device_id && (
                <div className="hidden md:block mb-4 p-3 border rounded-lg bg-muted/30">
                  <p className="text-sm text-muted-foreground">
                    Ready to add user to device: <strong>{deviceGroup.device_name || deviceGroup.device_id}</strong>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Use the form at the top of the page to add a new user assignment.
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onQuickAddDeviceChange?.(null)}
                    className="mt-2"
                  >
                    Dismiss
                  </Button>
                </div>
              )}
              {deviceGroup.assignments.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-2">No users assigned to this device</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickAddUser(deviceGroup.device_id, deviceGroup.device_name || deviceGroup.device_id)}
                    className="flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    Add First User
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {deviceGroup.assignments.map((assignment) => (
                    <div key={assignment.id} className={`${expandedCards.has(deviceGroup.device_id) ? 'block' : 'hidden'} md:flex items-center justify-between p-3 border rounded-lg`}>
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="font-medium">{assignment.display_name}</p>
                          <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
                            <span>Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}</span>
                            {assignment.user_role && (
                              <Badge variant="outline" className="text-xs">
                                {assignment.user_role}
                              </Badge>
                            )}
                            {assignment.user_organization && (
                              <Badge variant="secondary" className="text-xs">
                                {assignment.user_organization}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Mobile expanded assignment details */}
                          {expandedCards.has(deviceGroup.device_id) && (
                            <div className="md:hidden mt-1 space-y-1">
                              <p className="text-xs text-muted-foreground">
                                Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}
                              </p>
                              <div className="flex gap-1">
                                {assignment.user_role && (
                                  <Badge variant="outline" className="text-xs">
                                    {assignment.user_role}
                                  </Badge>
                                )}
                                {assignment.user_organization && (
                                  <Badge variant="secondary" className="text-xs">
                                    {assignment.user_organization}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <AlertDialog open={deleteId === assignment.id} onOpenChange={(open) => !open && setDeleteId(null)}>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setDeleteId(assignment.id)}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete this device assignment for {assignment.display_name}. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteAssignment(assignment.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {(!filteredGroupedAssignments || filteredGroupedAssignments.length === 0) && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">No devices found matching the current filters.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};